import * as React from 'react';
import * as _ from 'underscore';

import {
  DropdownButtonType,
  SingleSelectDropdown,
  TabStyles
} from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import { Location } from '../../common/react/Location';
import { FavoritesMenuNavigateToFavoriteEvent, FavoritesMenuOpenEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { imgPath } from '../../common/utilities/ImgUrl';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { getFavoritesSpaceDefaultSortOrder } from '../../ts/models/sort/FavoritesSortOptions';
import { Favorites } from '../../ts/resources/Favorites';
import { IFavorite } from '../../ts/ServerApi/types/IFavorite';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { ServiceLocator } from '../ServiceLocator';
import { SpacesSiteUrls } from '../spaces/SpacesSiteUrls';
import { FavoritesList, getFavoriteKey } from './FavoritesList';
import { HeaderStyles } from './HeaderStyles';

// we just want to grab all favorites, we usually use 1000 as max number of items to fetch
const PAGESIZE_MAX_SLICE: number = 1000;

export interface FavoritesDropdownOwnProps {
  readonly siteUrls: SpacesSiteUrls;
}

export interface FavoritesDropdownPropsFromAngular {
  readonly favoritesService: Favorites;
}

export type FavoritesDropdownProps = FavoritesDropdownOwnProps & FavoritesDropdownPropsFromAngular;

export interface FavoritesDropdownState {
  readonly favorites: IFavorite[];
  readonly favoritesRequestComplete: boolean;
  readonly buttonHover: boolean;
  readonly panelOpened: boolean;
}

export class FavoritesDropdown extends React.Component<FavoritesDropdownProps, FavoritesDropdownState> {
  private unmounted: boolean = false;

  public constructor(props: FavoritesDropdownProps) {
    super(props);
    this.state = {favorites: [], favoritesRequestComplete: false, buttonHover: false, panelOpened: false};
  }

  public componentWillUnmount() {
    this.unmounted = false;
  }

  public render(): JSX.Element {
    return (
      <div className={'tb-favorites-dropdown'} style={DropdownContainerStyle}>
        <div onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
          <SingleSelectDropdown
            children={this.getButtonContent()}
            type={DropdownButtonType.Text}
            list={this.getList()}
            value={''}
            testId="favorites-menu"
            onOpen={this.handleOpen}
            onClose={this.handleClose}
            onChange={this.handleChange}
            label={''}
            aria-controls={'favorites-menu-popup'}
          />
          </div>
      </div>
    );
  }

  private getList = () => {
    return (
      <FavoritesList
        id={'favorites-menu-popup'}
        onSelect={this.handleChange}
        urlForFavorite={this.urlForFavorite}
        items={this.state.favorites}
        favoritesRequestCompleted={this.state.favoritesRequestComplete} />
    );
  }

  private closePanel: () => void;

  private handleOpen = (done: () => void): void => {
    // save close panel for later
    this.closePanel = done;
    window.addEventListener('blur', this.onWindowBlur);

    this.props.favoritesService
      .all()
      .withOrder(FeatureFlags.reorderFavoritesEnabled? getFavoritesSpaceDefaultSortOrder() : [])
      .getSlice(0, PAGESIZE_MAX_SLICE)
      .then((result) => {
        Telemetry.sendEvent<FavoritesMenuOpenEvent>({
          type: TelemetryEvents.FavoritesMenuOpen,
          favoriteItemCount: result.total,
        });
        this.updateState({favorites: result.items, favoritesRequestComplete: true, panelOpened: true});
      }).catch(() => {
        this.updateState({favoritesRequestComplete: true, panelOpened: true});
      });
  }

  private handleClose = (): void => {
    if (this.closePanel) {
      this.closePanel();
      this.closePanel = null;
      this.updateState({panelOpened: false});
    }
  }

  private onWindowBlur = (): void => {
    this.handleClose();
    window.removeEventListener('blur', this.onWindowBlur);
  }

  private urlForFavorite = (favorite: IFavorite): string => {
    let redirectUrl = '';
    switch(favorite.objectType) {
      case 'datasource':
        if (favorite.objectId === '') {
          redirectUrl = this.props.siteUrls.favoriteDatasourceDoesntExistUrl(favorite.objectIdForFavoriting);
        } else  {
          redirectUrl = this.props.siteUrls.favoriteDatasourceUrl(favorite.objectId, favorite.objectIdForFavoriting);
        }
        break;

      case 'project':
        redirectUrl = this.props.siteUrls.favoriteProjectUrl(favorite.objectId);
        break;

      case 'workbook':
        redirectUrl = this.props.siteUrls.favoriteWorkbookUrl(favorite.objectId);
        break;

      case 'view':
        redirectUrl = this.props.siteUrls.redirectToFavoriteViewUrl(favorite.objectId);
        break;

      case 'flow':
        redirectUrl = this.props.siteUrls.favoriteFlowUrl(favorite.objectId);
        break;

      case 'dataRole':
        redirectUrl = this.props.siteUrls.redirectToDataRoleUrl(favorite.objectId);
        break;

      case 'metric':
        redirectUrl = this.props.siteUrls.favoriteMetricUrl(favorite.objectId);
        break;

      case 'publishedConnection':
        redirectUrl = this.props.siteUrls.favoritePublishedConnectionUrl(favorite.objectId);
        break;

      case 'collection':
        redirectUrl = this.props.siteUrls.collectionUrl(favorite.objectLuid);
        break;
    }

    return redirectUrl;
  }

  private handleChange = (value: string) => {
    const favorite = this.state.favorites.find((favorite) => {
      return getFavoriteKey(favorite) === value;
    });

    if (!favorite) {
      return;
    }

    const redirectUrl = this.urlForFavorite(favorite);
    if (redirectUrl) {
      Telemetry.sendEvent<FavoritesMenuNavigateToFavoriteEvent>({
        type: TelemetryEvents.FavoritesMenuNavigateToFavorite,
        favoriteItemType: favorite.objectType,
        favoriteItemId: favorite.objectIdForFavoriting, // TODO: once unified data sources have been sorted out, change this to objectId
      });
      Location.instance.setUrl(redirectUrl);
    }

    this.handleClose();
  }

  private onMouseEnter = () => {
    this.updateState({buttonHover: true});
  }

  private onMouseLeave = () => {
    this.updateState({buttonHover: false});
  }

  private getButtonContent = () => {
    const active = this.state.panelOpened || this.state.buttonHover;
    const imgSrc = active
      ? imgPath('spaces_toolbar_favorite_active.svg')
      : imgPath('spaces_toolbar_favorite.svg');

    return(
      <div style={{backgroundColor: TabStyles.Colors.ApplicationBlue, border: 'none', cursor: 'pointer', minHeight: 0, minWidth: 0}} data-tb-test-id={'favorites-dropdown-Button'}>
        <img
          style={{display:'block'}}
          src={imgSrc}
          width={24}
          height={24}
          title={this.closePanel ? Messages.favoritesMenuHide() : Messages.favoritesMenuShow()}
        />
      </div>
    );
  }

  private updateState<K extends keyof FavoritesDropdownState>(newState: Pick<FavoritesDropdownState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}

const DropdownContainerStyle: React.CSSProperties = {
  width: '100%',
  lineHeight: HeaderStyles.TopBarLineHeight,
  height: HeaderStyles.TopBarLineHeight,
  display: 'flex',
  alignItems: 'center',
};

export namespace FavoritesDropdownAngularProvider {
  interface AngularServices {
    readonly favorites: Favorites;
  }

  function getAngularServicesWithServiceLocator() {
    const favorites = ServiceLocator.get<Favorites>('Favorites');
    return {
      favorites,
    };
  }

  function selectPropsFromAngularServices(angularServices: AngularServices) {
    return {
      favoritesService: angularServices.favorites,
    };
  }

  export const HOC = angularProviderHOC<FavoritesDropdownOwnProps, FavoritesDropdownPropsFromAngular, AngularServices>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

export const PageHeaderFavoritesDropdown: React.ComponentType<FavoritesDropdownOwnProps> =
  FavoritesDropdownAngularProvider.HOC(FavoritesDropdown);
